import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import MyRentalsPage from './pages/MyRentalsPage';
import MyListingsPage from './pages/MyListingsPage';
import ListingRentalsPage from './pages/ListingRentalsPage';
import CreateListingPage from './pages/CreateListingPage';
import EditListingPage from './pages/EditListingPage';
import ListingWindowsPage from './pages/ListingWindowsPage';
import CreateWindowPage from './pages/CreateWindowPage';
import EditWindowPage from './pages/EditWindowPage';
import PublicListingPage from './pages/PublicListingPage';
import PreviewListingPage from './pages/PreviewListingPage';
import ReservationDetailPage from './pages/ReservationDetailPage';
import ListingCalendarPage from './pages/ListingCalendarPage';
import ListingQRCodePage from './pages/ListingQRCodePage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import PostCheckoutPage from './pages/PostCheckoutPage';
import CheckoutCompletePage from './pages/CheckoutCompletePage';
import BecomeOwnerPage from './pages/BecomeOwnerPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
import ConnectPage from './pages/ConnectPage';
import ProtectedRoute from './components/ProtectedRoute';

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Layout>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/connect" element={<ConnectPage />} />
            <Route path="/:listingName/reserve/:windowId/:date" element={<ReservationDetailPage />} />
            <Route path="/:listingName" element={<PublicListingPage />} />
            <Route path="/preview/:listingId" element={<PreviewListingPage />} />

            {/* Protected routes */}
            <Route
              path="/my-rentals"
              element={
                <ProtectedRoute>
                  <MyRentalsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-listings"
              element={
                <ProtectedRoute requireOwner>
                  <MyListingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/listings/:id/rentals"
              element={
                <ProtectedRoute requireOwner>
                  <ListingRentalsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/listings/create"
              element={
                <ProtectedRoute>
                  <CreateListingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/listings/:id/edit"
              element={
                <ProtectedRoute requireOwner>
                  <EditListingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/listings/:listingId/windows"
              element={
                <ProtectedRoute requireOwner>
                  <ListingWindowsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/listings/:listingId/windows/create"
              element={
                <ProtectedRoute requireOwner>
                  <CreateWindowPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/listings/:listingId/windows/:windowId/edit"
              element={
                <ProtectedRoute requireOwner>
                  <EditWindowPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/listings/:id/calendar"
              element={
                <ProtectedRoute>
                  <ListingCalendarPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/listings/:id/qr-code"
              element={
                <ProtectedRoute requireOwner>
                  <ListingQRCodePage />
                </ProtectedRoute>
              }
            />
            <Route path="/cart" element={<CartPage />} />
            {/* Checkout routes allow guests through: the backend resolves an
                unauthenticated caller to a guest cart id instead of rejecting
                the request (see cartIdentity.ts and the guest checkout plan). */}
            <Route
              path="/checkout"
              element={
                <ProtectedRoute allowGuest>
                  <CheckoutPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout/success"
              element={
                <ProtectedRoute allowGuest>
                  <PostCheckoutPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout/complete"
              element={
                <ProtectedRoute allowGuest>
                  <CheckoutCompletePage />
                </ProtectedRoute>
              }
            />
            <Route path="/become-owner" element={<BecomeOwnerPage />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDashboardPage />
                </ProtectedRoute>
              }
            />
          </Routes>
        </Layout>
      </Router>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
      />
    </ErrorBoundary>
  );
};

export default App;

